'use client';

import { ListingPage } from '@components/SearchV2/ListingPage';
import { SearchV2Page } from '@components/SearchV2/SearchV2Page';
import { hasLegacyCoveoHash } from '@lib/coveo/legacyHash';
import { useEffect, useRef } from 'react';

type CsrFallbackProps = {
  /** Full search state in the CSR hash encoding — see toLegacyHashFragment. */
  fragment?: string;
  /**
   * Degrade to the CSR listing page (its own engine, on the listing hub)
   * rather than the CSR search page. The hash encoding is shared: the CSR
   * engine reads the flat `f-ec_category` facet too (useUrlManagerSync
   * translates the v1 `cf-` form into it), so the seeded fragment restores the
   * category on either surface.
   */
  isListing?: boolean;
};

/** Write `fragment` to the hash, leaving an authoritative CSR hash alone. */
const seedLegacyHash = (fragment?: string) => {
  if (!fragment || hasLegacyCoveoHash()) {
    return;
  }
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}#${fragment}`
  );
};

/**
 * Query-param → hash bridge for every render of the CSR page under the
 * `as-search-ssr` roll-out: the degrade path for SSR failures (missing token,
 * failed/errored static fetch) and the control-variant branch of the /search
 * route, where a recipient of a treatment user's shared `?q=…&f-…` URL is
 * served CSR. `isListing` picks which CSR page the degrade lands on, since the
 * two surfaces run on different hubs. The legacy engine restores its state
 * exclusively from the URL hash (useUrlManagerSync), so either case would
 * otherwise silently degrade to browse-all results. Seed the hash with the
 * full search state (query, facets, sort, page) during render — not in an
 * effect — so it is in place before the engine's effects read
 * window.location.hash (all render passes complete before any effect runs),
 * then again from a post-commit microtask, which is the write the App Router
 * keeps (see the effect below). Seeded once per mount: an RSC re-render of the
 * same page (router.refresh on a currency change) re-delivers the original
 * request's searchParams, and re-seeding them after the user moved the session
 * on — e.g. cleared the search, leaving no hash — would advertise state the
 * engine no longer has, which a later back or reload would then restore. Only
 * a hash already carrying legacy Coveo params is authoritative and left alone
 * — which also keeps the replaceState idempotent under StrictMode's double
 * render, the seeded fragment being a Coveo hash itself. A plain anchor like `#main` is replaced:
 * the engine reads state exclusively from the hash, so keeping the anchor
 * would discard the whole search state. The query string is left in the URL so
 * a reload retries the SSR path (on the control branch a reload re-seeds the
 * same hash, a no-op under the authoritative-hash guard); it goes stale as the
 * session moves on (the CSR engine writes only the hash), which is safe because
 * migrateLegacyCoveoHash lets the hash win the merge on the next SSR load.
 * Interim bridge until the URL-state slice unifies the two URL schemes.
 */
export const CsrFallback = ({ fragment, isListing }: CsrFallbackProps) => {
  const seededRef = useRef(false);
  const mountFragmentRef = useRef(fragment);
  if (typeof window !== 'undefined' && !seededRef.current) {
    seededRef.current = true;
    seedLegacyHash(mountFragmentRef.current);
  }

  useEffect(() => {
    /**
     * The render-time seed lands before the App Router claims the history
     * entry, so Next throws it away: HistoryUpdater replaces the URL with the
     * canonicalUrl captured when AppRouter first rendered — the server URL,
     * which can never carry a hash — in an insertion effect, i.e. after every
     * render of the commit and before every effect of it. Seed again from a
     * microtask, which runs once the commit's effects have all flushed: the
     * App Router has patched history by then, so this write is the one it
     * adopts as canonical and stops reverting. The CSR engine reads the hash
     * only after its own async init (useEngineState), so it still restores
     * from the seed.
     */
    queueMicrotask(() => seedLegacyHash(mountFragmentRef.current));
  }, []);

  return isListing ? <ListingPage /> : <SearchV2Page />;
};
